import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import DataVisualizer from "../components/DataVisualizer";
import KPIBubbles from "../components/KPIBubbles";
import ShareLinksList from "../components/ShareLinksList";
import CreativeGallery from "../components/CreativeGallery";
import Recommendations from "../components/Recommendations";
import SmsEmailDashboard from "../components/SmsEmailDashboard";
const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px 20px;
`;

const Header = styled.header`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 28px;
  color: #333;
  margin-bottom: 10px;
`;

const Subtitle = styled.div`
  font-size: 16px;
  color: #666;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
`;

const InfoBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #f5f5f5;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 14px;
  color: #555;
`;

const BackButton = styled.button`
  background-color: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 20px;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 15px;
  border-radius: 5px;
  text-align: center;
  margin-bottom: 20px;
`;

const DateRangeBadge = styled(InfoBadge)`
  background-color: #e8f0fe;
  color: #1a73e8;
`;

const AdvertiserBadge = styled(InfoBadge)`
  background-color: #e6f4ea;
  color: #137333;
`;

const SourceBadge = styled(InfoBadge)`
  background-color: ${(props) =>
    props.source === "xandr" ? "#fef7e0" : "#fce8e6"};
  color: ${(props) => (props.source === "xandr" ? "#b06000" : "#d93025")};
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 8px;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 200px;
`;

const FilterLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 14px;
  color: #666;
`;

const ShareButton = styled.button`
  background-color: #6ed216;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-left: 10px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ActionButton = styled.button`
  background-color: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  cursor: pointer;
  font-size: 14px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background-color: #e0e0e0;
  }
`;
// Ajoutez ces styles styled-components après les autres définitions de style

const ConversionRateForm = styled.form`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f0f7ff;
  border-radius: 8px;
  border-left: 4px solid #1a73e8;
`;

const ConversionRateLabel = styled.label`
  font-size: 14px;
  color: #555;
  font-weight: 500;
`;

const ConversionRateInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  width: 120px;
  &:focus {
    outline: none;
    border-color: #1a73e8;
    box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
  }
`;

const ConversionRateButton = styled.button`
  background-color: #1a73e8;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 15px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0d62c9;
  }

  &:active {
    background-color: #0b57b7;
  }
`;

const ConversionRateInfo = styled.div`
  margin-left: auto;
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 5px;
`;

function DashboardPage({ user, onLogout, ...props }) {
  const { reportId } = useParams();
  const [reportData, setReportData] = useState(null);
  const [processedData, setProcessedData] = useState([]);
  const [selectedKPI, setSelectedKPI] = useState("impressions");
  const [selectedFilter, setSelectedFilter] = useState("day");
  const [recommendations, setRecommendations] = useState([]);
  const [selectedInsertionOrder, setSelectedInsertionOrder] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [showShareLinks, setShowShareLinks] = useState(false);
  const [conversionRate, setConversionRate] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  const apiUrl =
    process.env.REACT_APP_API_URL || "https://report-campaign.sirdata.io";
  const createShareLink = async () => {
    try {
      setShareLoading(true);
      setRecommendations([]);
      // Récupérer le token depuis le localStorage
      const token = localStorage.getItem("token");

      // Vérifier si le token existe
      if (!token) {
        setShareLoading(false);
        return;
      }

      const response = await axios.post(
        `${apiUrl}/api/share/create/${reportId}`,
        {
          insertionOrderId: selectedInsertionOrder,
          conversionRate: conversionRate, // Ajouter le taux de conversion
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setShareUrl(response.data.shareUrl);
      } else {
      }
    } catch (error) {
      console.error("Erreur lors de la création du lien de partage:", error);
    } finally {
      setShareLoading(false);
    }
  };
  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/api/reports/${reportId}`);

        if (response.data.success) {
          const report = response.data.report;

          // Si c'est un rapport partagé et qu'il y a un insertionOrderId spécifié
          if (response.data.shared && response.data.insertionOrderId) {
            setSelectedInsertionOrder(response.data.insertionOrderId);
          }

          // Vérifier la structure des données
          if (report.data && report.data.length > 0) {
            // Traiter les données pour les rendre compatibles avec les composants
            const processed = report.data.map((item) => {
              // Extraire les valeurs de base avec des valeurs par défaut
              const impressions = Number(item.impressions || item.imps || 0);
              const clicks = Number(item.clicks || 0);
              const viewableImpressions = Number(item.measurableImpressions);
              const measurableImpressions = Number(
                item.measurableImpressions || item.imps_viewed || 0
              );

              // Calculer le CTR directement
              const calculatedCTR =
                impressions > 0 ? (clicks / impressions) * 100 : 0;

              // Calculer la visibilité directement à partir des impressions visibles et totales
              let calculatedViewability = 0;
              if (impressions > 0 && viewableImpressions > 0) {
                calculatedViewability =
                  (viewableImpressions / impressions) * 100;
              }

              const processedItem = {
                date: item.date,
                creativeId: item.creativeId || item.creative_id || "",
                creativeName: item.creativeName || item.creative_name || "",
                creative:
                  item.creativeName ||
                  item.creative_name ||
                  `Creative ${
                    item.creativeId || item.creative_id || "Unknown"
                  }`,
                size: item.size || "",
                impressions: impressions,
                clicks: clicks,
                ctr: calculatedCTR,
                conversions: Number(item.conversions || item.total_convs || 0),
                budget: Number(item.budget || item.cost || 0) * conversionRate,
                viewableImpressions: viewableImpressions,
                measurableImpressions: measurableImpressions,
                viewability: calculatedViewability,
                insertionOrderId: item.insertionOrderId || null,
                insertionOrderName: item.insertionOrderName || "Inconnu",
              };
              return processedItem;
            });
            setProcessedData(processed);
          } else {
            console.warn(
              "Aucune donnée dans le rapport ou structure incorrecte"
            );
          }

          setReportData(report);
        } else {
          setError("Erreur lors de la récupération des données du rapport");
        }
      } catch (err) {
        if (err.response && err.response.status === 401) {
          // Si erreur d'authentification, déconnecter l'utilisateur
          onLogout();
          navigate("/login");
        } else {
          // Gérer les autres erreurs
          setError(`Erreur: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [reportId, apiUrl, conversionRate]);
  // Extraire les insertion orders uniques
  const insertionOrders = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];

    const uniqueOrders = [
      ...new Set(
        processedData
          .filter((item) => item.insertionOrderId && item.insertionOrderName)
          .map((item) =>
            JSON.stringify({
              id: item.insertionOrderId,
              name: item.insertionOrderName,
            })
          )
      ),
    ].map((item) => JSON.parse(item));

    return uniqueOrders;
  }, [processedData]);

  // Filtrer les données en fonction de l'insertion order sélectionné
  const filteredData = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];

    if (selectedInsertionOrder === "all") {
      return processedData;
    }

    return processedData.filter(
      (item) => item.insertionOrderId === selectedInsertionOrder
    );
  }, [processedData, selectedInsertionOrder, conversionRate]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR");
  };

  const displayDateRange = () => {
    if (reportData && reportData.dateRange) {
      return (
        <DateRangeBadge>
          <span>📅 Période: </span>
          <strong>
            {formatDate(reportData.dateRange.startDate)} au{" "}
            {formatDate(reportData.dateRange.endDate)}
          </strong>
        </DateRangeBadge>
      );
    }
    return null;
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case "xandr":
        return "Xandr";
      case "csv":
        return "CSV";
      default:
        return "Inconnu";
    }
  };

  const handleKPISelect = (kpi) => {
    setSelectedKPI(kpi);
  };

  const handleInsertionOrderChange = (e) => {
    setSelectedInsertionOrder(e.target.value);
  };

  const toggleShareLinks = () => {
    setShowShareLinks(!showShareLinks);
  };
  return (
    <DashboardContainer>
      <HeaderActions>
        <BackButton onClick={() => navigate(-1)}>← Retour</BackButton>
        
        {user && reportData?.source === "sms-email" && (
          <ShareButton 
            onClick={() => setIsEditing(true)} 
            style={{backgroundColor: '#f59e0b', marginRight: '10px'}}
          >
            ✏️ Modifier
          </ShareButton>
        )}

        <ShareButton onClick={createShareLink} disabled={shareLoading}>
          {shareLoading ? "Création..." : "🔗 Partager"}
        </ShareButton>
      </HeaderActions>

      {error && <ErrorContainer>{error}</ErrorContainer>}

      {loading ? (
        <LoadingContainer>
          Chargement des données du rapport...
        </LoadingContainer>
      ) : reportData ? (
        <>
          <Header>
            <Title>{reportData.campaignName || "Rapport de campagne"}</Title>
            <Subtitle>
              {reportData.advertiserName && (
                <AdvertiserBadge>
                  <span>🏢 Annonceur: </span>
                  <strong>{reportData.advertiserName}</strong>
                </AdvertiserBadge>
              )}
              {displayDateRange()}
              {reportData.source && (
                <SourceBadge source={reportData.source}>
                  <span>📊 Source: </span>
                  <strong>{getSourceLabel(reportData.source)}</strong>
                </SourceBadge>
              )}
              <InfoBadge>
                <span>📝 Lignes: </span>
                <strong>{filteredData.length}</strong>
              </InfoBadge>
              <InfoBadge>
                <span>🕒 Créé le: </span>
                <strong>{formatDate(reportData.createdAt)}</strong>
              </InfoBadge>
            </Subtitle>
          </Header>
          {/* Ajout du filtre par Insertion Order */}
          {insertionOrders.length > 0 && (
            <FiltersContainer>
              <FilterLabel>
                Filtrer par Insertion Order
                <FilterSelect
                  value={selectedInsertionOrder}
                  onChange={handleInsertionOrderChange}
                >
                  <option value="all">Tous les Insertion Orders</option>
                  {insertionOrders.map((order) => (
                    <option key={order.id} value={order.id}>
                      {order.name}
                    </option>
                  ))}
                </FilterSelect>
              </FilterLabel>
            </FiltersContainer>
          )}
          <ConversionRateForm
            onSubmit={(e) => {
              e.preventDefault();
              const value = parseFloat(e.target.conversionRate.value);
              if (!isNaN(value)) {
                setConversionRate(value);
              }
            }}
          >
            <ConversionRateLabel htmlFor="conversionRate">
              Taux de conversion (€):
            </ConversionRateLabel>
            <ConversionRateInput
              type="number"
              id="conversionRate"
              name="conversionRate"
              min="0"
              max="2"
              step="0.0001"
              defaultValue={conversionRate}
              placeholder="Entrez le taux"
            />
            <ConversionRateButton type="submit">Appliquer</ConversionRateButton>
            <ConversionRateInfo>
              <span>💡</span>
              <span>Taux actuel: {conversionRate}</span>
            </ConversionRateInfo>
          </ConversionRateForm>
          <ActionButton onClick={toggleShareLinks}>
            {showShareLinks
              ? "Masquer les liens de partage"
              : "Afficher les liens de partage"}
          </ActionButton>

          {showShareLinks && <ShareLinksList reportId={reportId} />}

          {filteredData.length > 0 && reportData?.source !== "sms-email" ? (
            <>
              <KPIBubbles
                data={filteredData}
                onKPISelect={handleKPISelect}
                selectedKPI={selectedKPI}
              />
              <DataVisualizer
                data={filteredData}
                selectedKPI={selectedKPI}
                selectedFilter={selectedFilter || "day"}
                onFilterChange={(filter) => setSelectedFilter(filter)}
              />
              <CreativeGallery data={filteredData} selectedKPI={selectedKPI} />
              <Recommendations recommendations={recommendations} />
            </>
          ) : reportData?.source === "sms-email" ? (
            <SmsEmailDashboard 
              reportData={reportData} 
              isEditing={isEditing}
              onSave={() => setIsEditing(false)}
              onCancel={() => setIsEditing(false)}
              onDataUpdate={(updatedReport) => setReportData({...reportData, ...updatedReport})}
              setReportData={setReportData}
              user={user}
            />
          ) : null}
        </>
      ) : (
        <ErrorContainer>Rapport non trouvé</ErrorContainer>
      )}
    </DashboardContainer>
  );
}

export default DashboardPage;
